import { Injectable, computed, inject, signal } from '@angular/core';
import MarkdownIt from 'markdown-it';

import {
  AccordionItem,
  CardReviewCriterion,
  CardReviewItem,
  CategorizationQuizLernbaustein,
  CarouselItem,
  ContentLernbaustein,
  CourseLernbaustein,
  LearningGoalSkill,
  LessonContent,
  LernbausteinSection,
  QuizLernbaustein,
  SkillProfileManifest,
  StoryTellingCarouselHeight,
  StoryTellingCarouselImageFit,
  StoryTellingCarouselItem,
  StoryTellingCarouselLayout,
  StoryTellingCarouselTextPosition,
  TabsItem,
} from '../models/course-content.models';
import { PRIMARY_COURSE_ID } from '../course.constants';
import { normalizeCourseId } from '../course-id.aliases';
import { CourseIndex, CourseIndexEntry } from '../models/course-index.models';
import { createStableId } from '../models/lernapp.models';
import type { CourseProgressStructure } from './learner-state.service';
import { CONTENT_PACKAGE_SOURCE } from './content-package-source';

const COURSE_INDEX_PATH = 'course-index.json';
const LEGACY_MANIFEST_PATH = 'content-manifest.json';

const tableMarkdown = new MarkdownIt({
  html: false,
  linkify: false,
  typographer: false,
});

export type ContentLanguage = 'DE' | 'EN';

export interface ExternalContentBlock {
  type: string;
  title: string | null;
  attributes: Record<string, string>;
  config: Record<string, unknown>;
  markdown: string;
  previewMissingAssets?: string[];
  previewElementDiagnostics?: Array<{
    message: string;
    hint: string | null;
    element: { kind: string; index: number; id?: string | null };
  }>;
}

export interface ExternalDocument {
  file: string;
  key: string;
  language: ContentLanguage;
  metadata: {
    title: string;
    status: string;
  };
  blocks: ExternalContentBlock[];
}

export interface ExternalLesson {
  id: string;
  key: string;
  order: number;
  titleByLanguage: Record<ContentLanguage, string | null>;
  files: Partial<Record<ContentLanguage, string>>;
  blocks: Array<{
    id: string;
    order: number;
    type: string;
    title: string | null;
    titleByLanguage?: Record<ContentLanguage, string | null>;
    kind?: 'module-overview' | 'module-intro' | 'lesson';
    taskId?: string | null;
    competencyId?: string | null;
    skillCount?: number;
    skillIds?: string[];
  }>;
}

export interface ExternalCompetency {
  id: string;
  level: number;
  titleByLanguage: Record<ContentLanguage, string | null>;
  descriptionByLanguage: Record<ContentLanguage, string | null>;
  taskIds: string[];
}

export interface ExternalCompetencyLevel {
  level: number;
  competencies: ExternalCompetency[];
}

export interface ExternalModule {
  id: string;
  key: string;
  order: number;
  titleByLanguage: Record<ContentLanguage, string | null>;
  moduleOverview: ExternalModuleOverview;
  lessons: ExternalLesson[];
}

export interface ExternalModuleOverview {
  id: string;
  role: 'module-overview';
  key: '00-moduluebersicht';
  titleByLanguage: Record<ContentLanguage, string | null>;
  files: Partial<Record<ContentLanguage, string>>;
  languageAvailability: ContentLanguage[];
  blockIds: string[];
  blocks: ExternalLesson['blocks'];
}

interface ContentManifest {
  documents: ExternalDocument[];
  skillProfile?: SkillProfileManifest;
  courseModel: {
    schemaVersion?: number;
    course: {
      id: string;
      titleByLanguage: Record<ContentLanguage, string | null>;
      competencyLevels?: ExternalCompetencyLevel[];
      modules: ExternalModule[];
    };
  };
}

@Injectable({ providedIn: 'root' })
export class CourseContentService {
  private readonly packageSource = inject(CONTENT_PACKAGE_SOURCE);
  readonly manifest = signal<ContentManifest | null>(null);
  readonly courseIndex = signal<CourseIndex | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly modules = computed(() => this.manifest()?.courseModel.course.modules ?? []);
  readonly courseId = computed(() => {
    const courseId = this.manifest()?.courseModel.course.id;
    return courseId ? normalizeCourseId(courseId) : null;
  });
  readonly competencyLevels = computed(
    () => this.manifest()?.courseModel.course.competencyLevels ?? [],
  );
  readonly skillProfile = computed(() => this.manifest()?.skillProfile ?? null);

  private loadPromise?: Promise<void>;
  private loadingCourseId?: string;
  private loadedCourseId?: string;
  private contentAssetBaseUrl = this.packageSource.assetBaseUrl(LEGACY_MANIFEST_PATH);

  load(courseId = PRIMARY_COURSE_ID): Promise<void> {
    const requestedCourseId = normalizeCourseId(courseId);
    if (this.loadedCourseId === requestedCourseId && this.manifest()) {
      return Promise.resolve();
    }
    if (!this.loadPromise || this.loadingCourseId !== requestedCourseId) {
      this.loadingCourseId = requestedCourseId;
      this.loadPromise = this.fetchManifest(requestedCourseId);
    }
    return this.loadPromise;
  }

  courseTitle(language: ContentLanguage): string | null {
    const titles = this.manifest()?.courseModel.course.titleByLanguage;
    return titles?.[language] ?? titles?.DE ?? titles?.EN ?? null;
  }

  // Kursweite Assets (z. B. Level-Bilder) liegen im Asset-Ordner des ersten Moduls.
  courseAssetUrl(fileName: string): string | null {
    const firstLesson = this.modules()[0]?.lessons[0];
    const file = firstLesson?.files.DE ?? firstLesson?.files.EN;
    if (!file?.includes('/')) {
      return null;
    }
    const directory = file.slice(0, file.lastIndexOf('/') + 1);
    return `${this.contentAssetBaseUrl}${directory}assets/${fileName}`;
  }

  moduleTitle(module: ExternalModule, language: ContentLanguage): string {
    return module.titleByLanguage[language]
      ?? module.titleByLanguage.DE
      ?? module.titleByLanguage.EN
      ?? module.key;
  }

  lessonTitle(lesson: ExternalLesson, language: ContentLanguage): string {
    return lesson.titleByLanguage[language]
      ?? lesson.titleByLanguage.DE
      ?? lesson.titleByLanguage.EN
      ?? lesson.key;
  }

  lessonSkillIds(lesson: ExternalLesson, language: ContentLanguage): string[] {
    const manifest = this.manifest();
    const file = lesson.files[language] ?? lesson.files.DE ?? lesson.files.EN;
    const document = manifest?.documents.find((candidate) => candidate.file === file);
    const ids = new Set<string>();

    if (document) {
      for (const block of document.blocks) {
        if (block.type !== 'learning-goals') {
          continue;
        }
        for (const skill of this.toLearningGoalSkills(block.config['skills'])) {
          if (skill.id.trim()) {
            ids.add(skill.id);
          }
        }
      }
    }

    for (const block of lesson.blocks) {
      for (const skillId of block.skillIds ?? []) {
        if (skillId.trim()) {
          ids.add(skillId);
        }
      }
    }

    if (ids.size > 0) {
      return [...ids];
    }

    return Array.from(
      { length: lesson.blocks.reduce((count, block) => count + (block.skillCount ?? 0), 0) },
      (_item, index) => `legacy-skill-${index + 1}`,
    );
  }

  buildProgressStructure(): CourseProgressStructure | null {
    if (!this.manifest()) {
      return null;
    }

    return {
      modules: this.modules().map((module) => ({
        id: module.id,
        lessons: module.lessons.map((lesson) => ({
          id: lesson.id,
          blockIds: lesson.blocks
            .filter((block) => block.kind !== 'module-intro')
            .map((block) => block.id),
        })),
      })),
      tasks: this.modules().flatMap((module) =>
        module.lessons.flatMap((lesson) =>
          lesson.blocks
            .filter((block) => block.type === 'tasks' || block.type === 'task-hint')
            .map((block) => ({
              id: block.taskId ?? block.id,
              moduleId: module.id,
              lessonId: lesson.id,
              competencyId: block.competencyId,
            })),
        ),
      ),
      competencyLevels: this.competencyLevels().map((level) => ({
        level: level.level,
        competencies: level.competencies.map((competency) => ({
          id: competency.id,
          taskIds: competency.taskIds,
        })),
      })),
    };
  }

  findModule(moduleId: string): ExternalModule | null {
    return this.modules().find((module) => module.id === moduleId) ?? null;
  }

  findLesson(moduleId: string, lessonId: string): ExternalLesson | null {
    return this.findModule(moduleId)?.lessons.find((lesson) => lesson.id === lessonId) ?? null;
  }

  buildLessonContent(
    moduleId: string,
    lessonId: string,
    language: ContentLanguage,
  ): LessonContent | null {
    const lesson = this.findLesson(moduleId, lessonId);
    const manifest = this.manifest();
    if (!lesson || !manifest) {
      return null;
    }

    const file = lesson.files[language] ?? lesson.files.DE ?? lesson.files.EN;
    const document = manifest.documents.find((candidate) => candidate.file === file);
    if (!document) {
      return null;
    }

    return this.buildLessonContentFromDocument(document, lesson, manifest, language);
  }

  private buildLessonContentFromDocument(
    document: ExternalDocument,
    lesson: ExternalLesson,
    manifest: ContentManifest,
    requestedLanguage: ContentLanguage,
  ): LessonContent | null {
    try {
      return {
        id: createStableId(lesson.id),
        title: document.metadata.title,
        finishLabel: document.language === 'EN' ? 'Finish lesson' : 'Lektion abschließen',
        lernbausteine: this.toLektionsbausteine(document, lesson),
        resolvedLanguage: document.language,
      };
    } catch (error) {
      if (requestedLanguage === 'DE' || document.language === 'DE') {
        throw error;
      }
      console.warn(
        `Inhalt "${document.file}" (${document.language}) konnte nicht aufgebaut werden, `
          + `Rückfall auf Deutsch: ${error instanceof Error ? error.message : String(error)}`,
      );
      const fallbackFile = lesson.files.DE;
      const fallbackDocument = manifest.documents.find((candidate) => candidate.file === fallbackFile);
      return fallbackDocument
        ? this.buildLessonContentFromDocument(fallbackDocument, lesson, manifest, 'DE')
        : null;
    }
  }

  buildModuleOverviewContent(moduleId: string, language: ContentLanguage): LessonContent | null {
    const module = this.findModule(moduleId);
    const manifest = this.manifest();
    if (!module || !manifest) {
      return null;
    }

    const document = this.findModuleOverviewDocument(module, language);
    if (!document) {
      return null;
    }

    const moduleOverview = this.toModuleOverviewLesson(module);
    return this.buildModuleOverviewContentFromDocument(document, module, moduleOverview, manifest, language);
  }

  private buildModuleOverviewContentFromDocument(
    document: ExternalDocument,
    module: ExternalModule,
    moduleOverview: ExternalLesson,
    manifest: ContentManifest,
    requestedLanguage: ContentLanguage,
  ): LessonContent | null {
    try {
      return {
        id: createStableId(moduleOverview.id),
        title: document.metadata.title || this.moduleTitle(module, requestedLanguage),
        finishLabel: document.language === 'EN' ? 'Start with lesson 1' : 'Mit Lektion 1 starten',
        lernbausteine: this.toLektionsbausteine(document, moduleOverview),
        resolvedLanguage: document.language,
      };
    } catch (error) {
      if (requestedLanguage === 'DE' || document.language === 'DE') {
        throw error;
      }
      console.warn(
        `Modulübersicht "${document.file}" (${document.language}) konnte nicht aufgebaut werden, `
          + `Rückfall auf Deutsch: ${error instanceof Error ? error.message : String(error)}`,
      );
      const fallbackFile = module.moduleOverview.files.DE;
      const fallbackDocument = manifest.documents.find((candidate) => candidate.file === fallbackFile);
      return fallbackDocument
        ? this.buildModuleOverviewContentFromDocument(fallbackDocument, module, moduleOverview, manifest, 'DE')
        : null;
    }
  }

  private findModuleOverviewDocument(
    module: ExternalModule,
    language: ContentLanguage,
  ): ExternalDocument | null {
    const manifest = this.manifest();
    if (!manifest) {
      return null;
    }

    const file = module.moduleOverview.files[language]
      ?? module.moduleOverview.files.DE
      ?? module.moduleOverview.files.EN;
    return file
      ? manifest.documents.find((candidate) => candidate.file === file) ?? null
      : null;
  }

  private toModuleOverviewLesson(module: ExternalModule): ExternalLesson {
    return {
      id: module.moduleOverview.id,
      key: module.moduleOverview.key,
      order: 0,
      titleByLanguage: module.moduleOverview.titleByLanguage,
      files: module.moduleOverview.files,
      blocks: module.moduleOverview.blocks,
    };
  }

  private toLektionsbausteine(
    document: ExternalDocument,
    lesson: ExternalLesson,
  ): CourseLernbaustein[] {
    const groups = new Map<string, Array<{ block: ExternalContentBlock; blockId?: string }>>();
    for (const [index, block] of document.blocks.entries()) {
      const blockId = lesson.blocks[index]?.id;
      const groupId = block.attributes['lesson-block-id'] ?? blockId
        ?? `block-${document.file}-${block.title ?? block.type}`;
      const group = groups.get(groupId) ?? [];
      group.push({ block, blockId });
      groups.set(groupId, group);
    }

    const lernbausteine = [...groups.entries()].map(([groupId, entries], groupIndex) => {
      const first = entries[0].block;
      const id = createStableId(lesson.blocks[groupIndex]?.id ?? groupId);
      const firstExplicitTitle = first.attributes['lesson-block-title'];
      const title = firstExplicitTitle?.trim()
        ? firstExplicitTitle
        : first.title ?? undefined;
      const blockKind = entries.find((entry) => entry.block.attributes['lesson-block-kind'])
        ?.block.attributes['lesson-block-kind'];
      const manifestKind = lesson.blocks[groupIndex]?.kind;
      const kind = manifestKind === 'module-overview'
        ? 'module-overview'
        : blockKind === 'module-intro' || manifestKind === 'module-intro'
          ? 'module-intro'
          : 'lesson';
      const quizzes = entries.filter(
        (entry) => entry.block.type === 'quiz' || entry.block.type === 'categorization-quiz',
      );
      if (quizzes.length > 0) {
        if (quizzes.length !== 1) {
          throw new Error(`Lektionsbaustein "${groupId}" darf genau ein Quiz enthalten.`);
        }
        const quizIndex = entries.findIndex((entry) => entry.block === quizzes[0].block);
        const quiz = quizzes[0].block.type === 'categorization-quiz'
          ? this.toCategorizationQuiz(quizzes[0].block, id, kind, document.language)
          : this.toQuiz(quizzes[0].block, id, kind, document.language);
        quiz.componentTitle = quiz.title;
        const beforeQuizEntries = entries.slice(0, quizIndex);
        const summaryBeforeQuizEntries = beforeQuizEntries.filter(({ block }) =>
          this.isLearningGoalsSummary(block),
        );
        quiz.title = title ?? quiz.title;
        quiz.notes = beforeQuizEntries
          .filter(({ block }) => !this.isLearningGoalsSummary(block))
          .map(({ block }) => this.toSection(
            block,
            document.file,
            id,
            document.language,
          ));
        quiz.afterNotes = [
          ...entries.slice(quizIndex + 1),
          ...summaryBeforeQuizEntries,
        ]
          .map(({ block }) => this.toSection(
            block,
            document.file,
            id,
            document.language,
          ));
        return quiz;
      }

      return {
        id,
        type: 'content',
        title,
        kind,
        sections: entries.map(({ block }) => this.toSection(
          block,
          document.file,
          id,
          document.language,
        )),
      } satisfies ContentLernbaustein;
    });

    return lernbausteine;
  }

  private toSection(
    block: ExternalContentBlock,
    documentFile: string,
    targetId?: string,
    language: ContentLanguage = 'DE',
  ): LernbausteinSection {
    if (block.type === 'tasks') {
      return {
        heading: block.title ?? (language === 'EN' ? 'Tasks' : 'Aufgaben'),
        targetId,
        blocks: [{ type: 'unordered-list' as const, items: this.parseTaskItems(block.markdown) }],
      };
    }
    if (block.type === 'image-with-text') {
      const imageUrl = block.attributes['imageUrl'];
      if (!imageUrl) {
        throw new Error(`Bild-Text-Block "${block.title ?? documentFile}" enthält keine imageUrl.`);
      }
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'image-with-text',
          text: this.resolveAssetPaths(block.markdown, documentFile),
          imageSrc: this.resolveAssetUrl(imageUrl, documentFile),
          imageAlt: block.attributes['alt'] ?? '',
          imagePosition: block.attributes['imagePosition'] === 'right' ? 'right' : 'left',
          ...(block.previewMissingAssets?.includes(imageUrl) ? {
            previewError: `Das referenzierte Bild "${imageUrl}" fehlt. Füge die Datei hinzu oder korrigiere den Pfad.`,
          } : {}),
        }],
      };
    }
    if (block.type === 'interactive-component' && block.attributes['componentType'] === 'carousel') {
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'carousel',
          items: this.toCarouselItems(this.resolveAssetPaths(block.markdown, documentFile)),
        }],
      };
    }
    if (
      block.type === 'interactive-component'
      && block.attributes['componentType'] === 'story-telling-carousel'
    ) {
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'story-telling-carousel',
          height: this.toStoryTellingCarouselHeight(block.attributes['height']),
          items: this.toStoryTellingCarouselItems(
            this.resolveAssetPaths(block.markdown, documentFile),
          ),
        }],
      };
    }
    if (block.type === 'interactive-component' && block.attributes['componentType'] === 'tabs') {
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'tabs',
          ariaLabel: block.title ?? undefined,
          items: this.toTabsItems(this.resolveAssetPaths(block.markdown, documentFile)),
        }],
      };
    }
    if (block.type === 'interactive-component' && block.attributes['componentType'] === 'accordion') {
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'accordion',
          items: this.toAccordionItems(this.resolveAssetPaths(block.markdown, documentFile)),
        }],
      };
    }
    if (block.type === 'card-review') {
      const intro = block.config['intro'];
      const outro = block.config['outro'];
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'card-review',
          intro: typeof intro === 'string' ? intro : undefined,
          outro: typeof outro === 'string' ? outro : undefined,
          items: this.toCardReviewItems(
            block.config['cards'],
            documentFile,
            block.previewElementDiagnostics ?? [],
            block.previewMissingAssets ?? [],
          ),
        }],
      };
    }
    if (block.type === 'learning-goals') {
      return {
        heading: undefined,
        blocks: [{
          type: 'learning-goals',
          variant: block.attributes['variant'] === 'summary' ? 'summary' : 'start',
          skills: this.toLearningGoalSkills(
            block.config['skills'],
            block.previewElementDiagnostics ?? [],
          ),
        }],
      };
    }
    if (block.type === 'code' || block.type === 'code-snippet') {
      const code = this.toCodeSnippet(block);
      return {
        heading: block.title ?? undefined,
        blocks: [{
          type: 'code-snippet',
          code: code.code,
          language: block.attributes['language'] ?? code.language,
          title: undefined,
          showLineNumbers: !this.isFalseAttribute(block.attributes['linenos']),
        }],
      };
    }
    const tone: LernbausteinSection['tone'] = block.type === 'warning'
      ? 'danger'
      : block.type === 'callout' || block.type === 'checkpoint'
        ? 'highlight'
        : block.type === 'exercise'
          ? 'info'
          : 'default';
    return {
      heading: block.title ?? undefined,
      tone,
      blocks: [{
        type: 'paragraph' as const,
        text: this.resolveAssetPaths(block.markdown, documentFile),
      }],
    };
  }

  private parseTaskItems(markdown: string): string[] {
    return [...markdown.matchAll(/^- \[([ xX])]\s+(.+)$/gm)].map(
      (match) => `[${match[1].toLowerCase()}] ${match[2].trim()}`,
    );
  }

  private toCardReviewItems(
    value: unknown,
    documentFile: string,
    diagnostics: ExternalContentBlock['previewElementDiagnostics'] = [],
    missingAssets: string[] = [],
  ): CardReviewItem[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((card): card is Record<string, unknown> => Boolean(card) && typeof card === 'object' && !Array.isArray(card))
      .map((card, index) => {
        const previewError = diagnostics?.find(
          (diagnostic) => diagnostic.element.index === index,
        )?.message ?? (typeof card['imageUrl'] === 'string' && missingAssets.includes(card['imageUrl'])
          ? `Das referenzierte Bild "${card['imageUrl']}" fehlt.`
          : undefined);
        return {
          id: String(card['id'] ?? ''),
          title: String(card['title'] ?? ''),
          text: String(card['text'] ?? ''),
          imageSrc: typeof card['imageUrl'] === 'string'
            ? this.resolveAssetUrl(card['imageUrl'], documentFile)
            : undefined,
          imageAlt: typeof card['imageAlt'] === 'string'
            ? String(card['imageAlt'])
            : undefined,
          categoryLabel: String(card['categoryLabel'] ?? ''),
          criteria: this.toCardReviewCriteria(card['criteria']),
          improvements: this.toStringList(card['improvements']),
          ...(previewError ? { previewError } : {}),
        };
      });
  }

  private toCardReviewCriteria(value: unknown): CardReviewCriterion[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((criterion): criterion is Record<string, unknown> => Boolean(criterion) && typeof criterion === 'object' && !Array.isArray(criterion))
      .map((criterion) => ({
        label: String(criterion['label'] ?? ''),
        met: Boolean(criterion['met']),
        explanation: String(criterion['explanation'] ?? ''),
      }));
  }

  private toStringList(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((item) => String(item ?? '')).filter((item) => item.trim().length > 0);
  }

  private toLearningGoalSkills(
    value: unknown,
    diagnostics: ExternalContentBlock['previewElementDiagnostics'] = [],
  ): LearningGoalSkill[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .filter((skill): skill is Record<string, unknown> => Boolean(skill) && typeof skill === 'object' && !Array.isArray(skill))
      .map((skill, index) => {
        const previewError = diagnostics?.find(
          (diagnostic) => diagnostic.element.index === index,
        )?.message;
        return {
          id: String(skill['id'] ?? ''),
          title: String(skill['title'] ?? ''),
          benefit: String(skill['benefit'] ?? ''),
          successCriterion: String(skill['successCriterion'] ?? ''),
          ...(previewError ? { previewError } : {}),
        };
      });
  }

  private isLearningGoalsSummary(block: ExternalContentBlock): boolean {
    return block.type === 'learning-goals' && block.attributes['variant'] === 'summary';
  }

  private toCarouselItems(markdown: string): CarouselItem[] {
    const table = this.parseMarkdownTable(markdown);
    if (table.rows.length === 0) {
      return [];
    }

    const imageColumnIndex = this.findCarouselColumn(table.headers, ['bild', 'image', 'img', 'bild url', 'image url']);
    const altColumnIndex = this.findCarouselColumn(table.headers, ['alt', 'alt text', 'alternativtext']);
    const sectionColumnIndex = this.findCarouselColumn(table.headers, ['abschnitt', 'section', 'gruppe', 'group']);
    const contentColumnIndexes = table.headers
      .map((_header, index) => index)
      .filter((index) => index !== imageColumnIndex && index !== altColumnIndex && index !== sectionColumnIndex);

    return table.rows.map((row) => {
      const contentCells = contentColumnIndexes.map((index) => row[index] ?? '');
      const section = sectionColumnIndex >= 0 ? row[sectionColumnIndex]?.trim() : '';
      const image = imageColumnIndex >= 0
        ? this.parseCarouselImage(row[imageColumnIndex] ?? '', row[altColumnIndex] ?? '')
        : null;
      const imageFields = image
        ? { imageSrc: image.src, imageAlt: image.alt }
        : {};
      const sectionField = section ? { section } : {};

      if (contentCells.length >= 3) {
        const fields = contentColumnIndexes.slice(3).map((columnIndex, index) => ({
            label: table.headers[columnIndex],
            value: contentCells[index + 3] ?? '',
          })).filter((field) => field.value.trim().length > 0);
        return {
          ...sectionField,
          eyebrow: contentCells[0],
          title: contentCells[1],
          body: contentCells[2],
          ...imageFields,
          fields,
        };
      }

      if (contentCells.length === 2) {
        return {
          ...sectionField,
          title: contentCells[0],
          body: contentCells[1],
          ...imageFields,
          fields: [],
        };
      }

      return {
        ...sectionField,
        title: contentCells[0] ?? '',
        ...imageFields,
        fields: [],
      };
    });
  }

  private toStoryTellingCarouselItems(markdown: string): StoryTellingCarouselItem[] {
    const table = this.parseMarkdownTable(markdown);
    if (table.rows.length === 0) {
      return [];
    }

    const imageColumnIndex = this.findCarouselColumn(table.headers, ['bild', 'image', 'img', 'bild url', 'image url']);
    const altColumnIndex = this.findCarouselColumn(table.headers, ['alt', 'alt text', 'alternativtext']);
    const layoutColumnIndex = this.findCarouselColumn(table.headers, ['layout', 'darstellung']);
    const textPositionColumnIndex = this.findCarouselColumn(table.headers, ['textposition', 'text position', 'overlay position']);
    const imageFitColumnIndex = this.findCarouselColumn(table.headers, ['bildmodus', 'image fit', 'object fit', 'fit']);
    const sectionColumnIndex = this.findCarouselColumn(table.headers, ['abschnitt', 'section', 'gruppe', 'group']);
    const eyebrowColumnIndex = this.findCarouselColumn(table.headers, ['kicker', 'eyebrow', 'label']);
    const titleColumnIndex = this.findCarouselColumn(table.headers, ['titel', 'title']);
    const bodyColumnIndex = this.findCarouselColumn(table.headers, ['text', 'body', 'beschreibung']);
    const linkLabelColumnIndex = this.findCarouselColumn(table.headers, ['link text', 'link label', 'cta']);
    const linkHrefColumnIndex = this.findCarouselColumn(table.headers, ['link url', 'link href', 'href', 'url']);
    const reservedColumnIndexes = new Set([
      imageColumnIndex,
      altColumnIndex,
      layoutColumnIndex,
      textPositionColumnIndex,
      imageFitColumnIndex,
      sectionColumnIndex,
      eyebrowColumnIndex,
      titleColumnIndex,
      bodyColumnIndex,
      linkLabelColumnIndex,
      linkHrefColumnIndex,
    ].filter((index) => index >= 0));

    return table.rows.map((row) => {
      const image = imageColumnIndex >= 0
        ? this.parseCarouselImage(row[imageColumnIndex] ?? '', row[altColumnIndex] ?? '')
        : null;
      const linkLabel = linkLabelColumnIndex >= 0 ? row[linkLabelColumnIndex]?.trim() : '';
      const linkHref = linkHrefColumnIndex >= 0 ? row[linkHrefColumnIndex]?.trim() : '';
      const section = sectionColumnIndex >= 0 ? row[sectionColumnIndex]?.trim() : '';
      const fields = table.headers
        .map((label, index) => ({ label, value: row[index] ?? '', index }))
        .filter((field) => !reservedColumnIndexes.has(field.index) && field.value.trim().length > 0)
        .map(({ label, value }) => ({ label, value }));

      return {
        ...(section ? { section } : {}),
        eyebrow: eyebrowColumnIndex >= 0 ? row[eyebrowColumnIndex]?.trim() : undefined,
        title: titleColumnIndex >= 0 ? row[titleColumnIndex]?.trim() || 'Story' : 'Story',
        body: bodyColumnIndex >= 0 ? row[bodyColumnIndex]?.trim() : undefined,
        imageSrc: image?.src,
        imageAlt: image?.alt,
        layout: this.toStoryTellingCarouselLayout(row[layoutColumnIndex] ?? ''),
        textPosition: this.toStoryTellingCarouselTextPosition(row[textPositionColumnIndex] ?? ''),
        imageFit: this.toStoryTellingCarouselImageFit(row[imageFitColumnIndex] ?? ''),
        linkLabel: linkLabel || undefined,
        linkHref: linkHref || undefined,
        fields,
      };
    });
  }

  private toStoryTellingCarouselLayout(value: string): StoryTellingCarouselLayout {
    return ['image-cover-overlay', 'image-left', 'image-right'].includes(value.trim())
      ? value.trim() as StoryTellingCarouselLayout
      : 'image-cover-overlay';
  }

  private toStoryTellingCarouselTextPosition(value: string): StoryTellingCarouselTextPosition {
    return ['bottom-left', 'bottom-right', 'center', 'top-left', 'top-right'].includes(value.trim())
      ? value.trim() as StoryTellingCarouselTextPosition
      : 'bottom-left';
  }

  private toStoryTellingCarouselImageFit(value: string): StoryTellingCarouselImageFit {
    return value.trim() === 'contain' ? 'contain' : 'cover';
  }

  private toStoryTellingCarouselHeight(value: string | undefined): StoryTellingCarouselHeight {
    return value === 'compact' || value === 'tall' ? value : 'standard';
  }

  private toTabsItems(markdown: string): TabsItem[] {
    const table = this.parseMarkdownTable(markdown);
    if (table.rows.length === 0) {
      return [];
    }

    const labelColumnIndex = this.findCarouselColumn(table.headers, [
      'tab',
      'reiter',
      'kriterium',
      'criterion',
      'label',
    ]);
    const titleColumnIndex = this.findCarouselColumn(table.headers, ['titel', 'title']);
    const bodyColumnIndex = this.findCarouselColumn(table.headers, [
      'text',
      'inhalt',
      'beschreibung',
      'body',
      'description',
    ]);
    const imageColumnIndex = this.findCarouselColumn(table.headers, [
      'bild',
      'image',
      'img',
      'bild url',
      'image url',
    ]);
    const altColumnIndex = this.findCarouselColumn(table.headers, ['alt', 'alt text', 'alternativtext']);

    return table.rows
      .map((row) => {
        const labelFromColumn = labelColumnIndex >= 0 ? row[labelColumnIndex]?.trim() : '';
        const titleFromColumn = titleColumnIndex >= 0 ? row[titleColumnIndex]?.trim() : '';
        const fallbackLabel = titleFromColumn || row.find((cell) => (cell ?? '').trim().length > 0)?.trim() || '';
        const label = labelFromColumn || fallbackLabel;
        const body = bodyColumnIndex >= 0
          ? row[bodyColumnIndex]?.trim() || ''
          : this.firstNonEmptyCell(row, [labelColumnIndex, titleColumnIndex, imageColumnIndex, altColumnIndex]);
        const title = titleFromColumn && titleFromColumn !== label ? titleFromColumn : undefined;
        const image = imageColumnIndex >= 0
          ? this.parseCarouselImage(row[imageColumnIndex] ?? '', row[altColumnIndex] ?? '')
          : null;

        return {
          label,
          title,
          body: body || undefined,
          imageSrc: image?.src,
          imageAlt: image?.alt,
        } satisfies TabsItem;
      })
      .filter((item) => item.label.trim().length > 0);
  }

  private toAccordionItems(markdown: string): AccordionItem[] {
    const normalized = markdown.replace(/\r\n?/g, '\n').trim();
    const headings = [...normalized.matchAll(/^###\s+(.+?)\s*$/gm)];

    return headings.map((heading, index) => {
      const title = heading[1].trim();
      const contentStart = (heading.index ?? 0) + heading[0].length;
      const contentEnd = headings[index + 1]?.index ?? normalized.length;
      const content = normalized.slice(contentStart, contentEnd).trim();
      const table = this.parseMarkdownTable(content);

      return {
        id: `accordion-item-${index}`,
        title,
        ...(table.rows.length > 0
          ? { carouselItems: this.toCarouselItems(content) }
          : { body: content }),
      };
    });
  }

  private firstNonEmptyCell(row: string[], excludedIndexes: number[]): string {
    const excluded = new Set(excludedIndexes.filter((index) => index >= 0));
    for (const [index, value] of row.entries()) {
      if (excluded.has(index)) {
        continue;
      }
      const trimmed = value?.trim() ?? '';
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
    return '';
  }

  private toCodeSnippet(block: ExternalContentBlock): { code: string; language?: string } {
    const markdown = block.markdown.trim();
    const fencePattern = /```([^\n`]*)\n([\s\S]*?)```/g;
    const fences = [...markdown.matchAll(fencePattern)];

    if (fences.length === 0) {
      return {
        code: block.markdown.replace(/\r\n?/g, '\n').trimEnd(),
        language: block.attributes['language'],
      };
    }

    const languages = fences
      .map((match) => match[1]?.trim())
      .filter((language): language is string => Boolean(language));
    const firstLanguage = languages[0];
    const sharedLanguage = firstLanguage && languages.every((language) => language === firstLanguage)
      ? firstLanguage
      : undefined;

    return {
      code: fences.map((match) => match[2].replace(/\r\n?/g, '\n').trimEnd()).join('\n\n'),
      language: block.attributes['language'] ?? sharedLanguage,
    };
  }

  private isFalseAttribute(value: string | undefined): boolean {
    return ['0', 'false', 'no', 'nein'].includes(value?.trim().toLowerCase() ?? '');
  }

  private findCarouselColumn(headers: string[], names: string[]): number {
    const normalizedNames = new Set(names.map((name) => this.normalizeCarouselHeader(name)));
    return headers.findIndex((header) => normalizedNames.has(this.normalizeCarouselHeader(header)));
  }

  private normalizeCarouselHeader(header: string): string {
    return header.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private parseCarouselImage(value: string, fallbackAlt: string): { src: string; alt: string } | null {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const markdownImage = trimmed.match(/^!\[([^\]]*)]\(([^)\s]+)(?:\s+"[^"]*")?\)$/);
    if (markdownImage) {
      return {
        src: markdownImage[2],
        alt: fallbackAlt.trim() || markdownImage[1].trim(),
      };
    }

    return {
      src: trimmed,
      alt: fallbackAlt.trim(),
    };
  }

  private parseMarkdownTable(markdown: string): { headers: string[]; rows: string[][] } {
    const tokens = tableMarkdown.parse(markdown, {});
    const headers: string[] = [];
    const rows: string[][] = [];
    let inHeader = false;
    let currentRow: string[] | null = null;
    let currentCell: string | null = null;

    for (const token of tokens) {
      switch (token.type) {
        case 'thead_open':
          inHeader = true;
          break;
        case 'thead_close':
          inHeader = false;
          break;
        case 'tr_open':
          currentRow = [];
          break;
        case 'th_open':
        case 'td_open':
          currentCell = '';
          break;
        case 'inline':
          if (currentCell !== null) {
            currentCell += token.content;
          }
          break;
        case 'th_close':
        case 'td_close':
          currentRow?.push((currentCell ?? '').trim());
          currentCell = null;
          break;
        case 'tr_close':
          if (currentRow && currentRow.some((cell) => cell.trim().length > 0)) {
            if (inHeader) {
              headers.splice(0, headers.length, ...currentRow);
            } else {
              rows.push(currentRow);
            }
          }
          currentRow = null;
          break;
      }
    }

    return { headers, rows };
  }

  private toQuiz(
    block: ExternalContentBlock,
    id: ReturnType<typeof createStableId>,
    kind: 'module-overview' | 'module-intro' | 'lesson' = 'lesson',
    language: ContentLanguage = 'DE',
  ): QuizLernbaustein {
    const isEnglish = language === 'EN';
    const optionPattern = /^- \[([ xX])]\s+(.+)$/gm;
    const options = [...block.markdown.matchAll(optionPattern)].map((match, index) => ({
      id: `${id}-option-${index + 1}`,
      label: match[2].trim(),
      isCorrect: match[1].toLowerCase() === 'x',
    }));
    const explanation = block.markdown.match(/\*\*Erkl[aä]rung:\*\*\s*(.+)$/im)?.[1]
      ?? block.markdown.match(/\*\*Explanation:\*\*\s*(.+)$/im)?.[1]
      ?? (isEnglish
        ? 'Compare your selection with the marked solution.'
        : 'Vergleiche deine Auswahl mit der markierten Lösung.');

    return {
      id,
      type: 'quiz',
      title: block.title ?? (isEnglish ? 'Self-check' : 'Selbstcheck'),
      kind,
      prompt: String(block.config['question'] ?? block.title ?? ''),
      instruction: block.attributes['mode'] === 'multiple'
        ? (isEnglish ? 'Choose all answers that apply.' : 'Wähle alle passenden Antworten.')
        : (isEnglish ? 'Choose one answer.' : 'Wähle eine Antwort.'),
      options,
      multiSelect: block.attributes['mode'] === 'multiple',
      successMessage: explanation,
      errorMessage: explanation,
    };
  }

  private toCategorizationQuiz(
    block: ExternalContentBlock,
    id: ReturnType<typeof createStableId>,
    kind: 'module-overview' | 'module-intro' | 'lesson' = 'lesson',
    language: ContentLanguage = 'DE',
  ): CategorizationQuizLernbaustein {
    const categories = Array.isArray(block.config['categories'])
      ? block.config['categories'] as Array<Record<string, unknown>>
      : [];
    const cards = Array.isArray(block.config['cards'])
      ? block.config['cards'] as Array<Record<string, unknown>>
      : [];
    return {
      id,
      type: 'categorization-quiz',
      title: block.title ?? (language === 'EN' ? 'Categorization quiz' : 'Kategorisierungsquiz'),
      kind,
      prompt: String(block.config['question'] ?? block.title ?? ''),
      feedbackTiming: block.attributes['feedbackTiming'] === 'end' ? 'end' : 'immediate',
      categories: categories.map((category) => ({
        id: String(category['id']),
        label: String(category['label']),
      })),
      cards: cards.map((card) => ({
        id: String(card['id']),
        text: String(card['text']),
        categoryId: String(card['categoryId']),
        explanation: String(card['explanation']),
      })),
    };
  }

  private resolveAssetPaths(markdown: string, documentFile: string): string {
    const directory = documentFile.includes('/')
      ? documentFile.slice(0, documentFile.lastIndexOf('/') + 1)
      : '';
    return markdown.replace(
      /(!?\[[^\]]*]\()\.\/([^)]+)(\))/g,
      `$1${this.contentAssetBaseUrl}${directory}$2$3`,
    );
  }

  private resolveAssetUrl(assetUrl: string, documentFile: string): string {
    if (!assetUrl.startsWith('./')) {
      return assetUrl;
    }
    const directory = documentFile.includes('/')
      ? documentFile.slice(0, documentFile.lastIndexOf('/') + 1)
      : '';
    return `${this.contentAssetBaseUrl}${directory}${assetUrl.slice(2)}`;
  }

  private async fetchManifest(courseId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    if (this.loadedCourseId !== courseId) {
      this.manifest.set(null);
    }
    try {
      const manifestSource = await this.resolveManifestSource(courseId);
      const response = await this.packageSource.readJson<ContentManifest>(manifestSource.path);
      if (!response.ok) {
        throw new Error(`Manifest "${manifestSource.path}" konnte nicht geladen werden: HTTP ${response.status}`);
      }
      const manifest = response.value as ContentManifest;
      this.assertValidManifest(manifest);
      this.contentAssetBaseUrl = manifestSource.assetBaseUrl;
      this.manifest.set(manifest);
      this.loadedCourseId = courseId;
    } catch (error) {
      this.manifest.set(null);
      this.contentAssetBaseUrl = this.packageSource.assetBaseUrl(LEGACY_MANIFEST_PATH);
      this.error.set(
        `Kursinhalte konnten nicht geladen werden: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      this.loadPromise = undefined;
      this.loadedCourseId = undefined;
    } finally {
      this.loading.set(false);
      this.loadingCourseId = undefined;
    }
  }

  private async resolveManifestSource(courseId: string): Promise<{
    path: string;
    assetBaseUrl: string;
  }> {
    const indexResponse = await this.packageSource.readJson<CourseIndex | ContentManifest>(
      COURSE_INDEX_PATH,
    );
    if (indexResponse.status === 404) {
      this.courseIndex.set(null);
      if (courseId !== PRIMARY_COURSE_ID) {
        throw new Error(
          `Kurs "${courseId}" kann ohne Course-Index nicht aus dem Legacy-Manifest geladen werden.`,
        );
      }
      return {
        path: LEGACY_MANIFEST_PATH,
        assetBaseUrl: this.packageSource.assetBaseUrl(LEGACY_MANIFEST_PATH),
      };
    }
    if (!indexResponse.ok) {
      throw new Error(`Course-Index konnte nicht geladen werden: HTTP ${indexResponse.status}`);
    }

    const index = indexResponse.value as CourseIndex | ContentManifest;
    if (this.isContentManifest(index)) {
      this.courseIndex.set(null);
      if (courseId !== PRIMARY_COURSE_ID) {
        throw new Error(
          `Kurs "${courseId}" kann ohne echten Course-Index nicht aus einem Legacy-Manifest geladen werden.`,
        );
      }
      return {
        path: COURSE_INDEX_PATH,
        assetBaseUrl: this.packageSource.assetBaseUrl(COURSE_INDEX_PATH),
      };
    }
    this.assertValidCourseIndex(index);
    this.courseIndex.set(index);
    const course = this.findIndexCourse(index, courseId);
    if (!course) {
      throw new Error(`Kurs "${courseId}" ist im Course-Index nicht vorhanden.`);
    }
    if (!course.manifestPath?.trim()) {
      throw new Error(`Kurs "${courseId}" besitzt im Course-Index keinen Manifestpfad.`);
    }
    const path = this.toContentPackagePath(course.manifestPath);
    return {
      path,
      assetBaseUrl: this.toAssetBaseUrl(path),
    };
  }

  private assertValidCourseIndex(index: CourseIndex): void {
    if (
      !index
      || typeof index.schemaVersion !== 'number'
      || typeof index.defaultCourseId !== 'string'
      || !Array.isArray(index.courses)
      || index.courses.length === 0
    ) {
      throw new Error('Course-Index enthält keine gültige Kursliste.');
    }
    for (const course of index.courses) {
      if (
        !course
        || typeof course.id !== 'string'
        || !course.id.trim()
        || (course.manifestPath !== undefined && typeof course.manifestPath !== 'string')
        || typeof course.status !== 'string'
        || !course.status.trim()
      ) {
        throw new Error('Course-Index enthält einen ungültigen Kurseintrag.');
      }
    }
  }

  private isContentManifest(value: CourseIndex | ContentManifest): value is ContentManifest {
    return Boolean(
      value
      && typeof value === 'object'
      && 'documents' in value
      && Array.isArray(value.documents)
      && 'courseModel' in value
      && value.courseModel?.course?.modules,
    );
  }

  private findIndexCourse(index: CourseIndex, courseId: string): CourseIndexEntry | null {
    const normalizedCourseId = normalizeCourseId(courseId);
    return index.courses.find((course) =>
      normalizeCourseId(course.id) === normalizedCourseId
        || course.legacyIds?.some((legacyId) => normalizeCourseId(legacyId) === normalizedCourseId),
    ) ?? null;
  }

  private toContentPackagePath(manifestPath: string): string {
    const normalized = manifestPath.trim().replace(/^\.?\//, '');
    if (
      normalized.startsWith('/')
      || normalized.includes('://')
      || normalized.split('/').some((part) => part === '..')
    ) {
      throw new Error(`Ungültiger Manifestpfad "${manifestPath}".`);
    }
    return normalized;
  }

  private toAssetBaseUrl(manifestPath: string): string {
    return this.packageSource.assetBaseUrl(manifestPath);
  }

  private assertValidManifest(manifest: ContentManifest): void {
    if (!manifest.courseModel?.course?.modules || !Array.isArray(manifest.documents)) {
      throw new Error('Manifest enthält kein gültiges Kursmodell.');
    }
    if (
      manifest.courseModel.schemaVersion !== undefined
      && manifest.courseModel.schemaVersion !== 3
    ) {
      throw new Error('Manifest verwendet nicht Course Model Version 3.');
    }
    if (manifest.courseModel.course.modules.some((module) =>
      module.moduleOverview?.role !== 'module-overview'
      || !module.moduleOverview.files
      || Object.keys(module.moduleOverview.files).length === 0,
    )) {
      throw new Error('Manifest enthält keine gültigen Modulübersichten.');
    }
  }
}
